import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ModalWrapper } from "./ModalWrapper";
import { ContextMenu } from "../ui/ContextMenu";
import { EditPatientModal } from "./EditPatientModal";
import { NewMeetingModal } from "./NewMeetingModal";
import { ViewMeetingsModal } from "./ViewMeetingsModal";
import { FinishTreatmentModal } from "./FinishTreatmentModal";
import { EditCommentsModal } from "./EditCommentsModal";
import { PaymentLinksModal } from "./PaymentLinksModal";
import { AdHocMeetingModal } from "./AdHocMeetingModal";
import { CallPatientConfModal } from "./CallPatientConfModal";
import { WhatsAppConversationPopup } from "../whatsapp/WhatsAppConversationPopup";
import { ManageColumnsModal, ColumnConfig } from "./ManageColumnsModal";
import { useClinics, useUsers } from "../../hooks/useClinic";
import { useTablePreferences } from "../../hooks/useTablePreferences";
import { normalizePhone } from "../../utils/phoneUtils";
import {
    PATIENT_COLUMNS,
    formatDate,
    calculateFinishReason,
    formatInvitationStatus,
    formatTasksNumber,
    formatNextMeetingDateTime,
    formatMeetingCount,
    getAgeGroupColor,
    formatSubClinic,
    shouldHighlightSubClinic,
    formatNotes,
    formatMoveType,
} from "../../utils/patientTableFormat";
import apiService from "../../services/api";
import { PatientSearchBy, User } from "../../types/api";
import { canScheduleMeeting } from '../../utils/permissions';

interface PatientSearchModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: User;
    onNavigate?: (
        screen: "calendar" | "patients" | "tasks" | "internal-tasks" | "patient-page" | "patient" | "users" | "connections" | "billing" | "whatsapp" | "adhoc-meetings" | "ai-support",
        patientId?: string | number,
        meetingId?: string | number
    ) => void;
}

// Shorter terms match too much of the clinic to be useful, so the Search button stays
// disabled below this length. The backend rejects them too.
const MIN_SEARCH_LENGTH = 3;

// The last search is remembered so reopening the popup - typically to look at the same
// patient again from another screen - starts where the user left off. Every Layout
// mounts its own copy of this modal, so the value is re-read on open rather than kept
// in component state.
const LAST_SEARCH_STORAGE_KEY = "patient-search-last";

const readLastSearch = (): { searchBy: PatientSearchBy; value: string } => {
    const empty = { searchBy: "name" as PatientSearchBy, value: "" };
    try {
        const raw = localStorage.getItem(LAST_SEARCH_STORAGE_KEY);
        if (!raw) return empty;
        const parsed = JSON.parse(raw);
        const searchBy = SEARCH_OPTIONS.some(o => o.value === parsed?.searchBy)
            ? (parsed.searchBy as PatientSearchBy)
            : empty.searchBy;
        return { searchBy, value: typeof parsed?.value === "string" ? parsed.value : "" };
    } catch (e) {
        console.error("[PatientSearchModal] Failed to read the last search:", e);
        return empty;
    }
};

const saveLastSearch = (searchBy: PatientSearchBy, value: string): void => {
    try {
        localStorage.setItem(LAST_SEARCH_STORAGE_KEY, JSON.stringify({ searchBy, value }));
    } catch (e) {
        console.error("[PatientSearchModal] Failed to save the last search:", e);
    }
};

const SEARCH_OPTIONS: { value: PatientSearchBy; label: string; placeholder: string }[] = [
    { value: "name", label: "Name", placeholder: "First or last name" },
    { value: "hm_id", label: "HM ID", placeholder: "HM ID" },
    { value: "tz", label: "TZ", placeholder: "National ID (TZ)" },
    { value: "phone", label: "Phone", placeholder: "Phone number" },
];

// Every column the patients table offers, plus Phone, which only the search results
// show - a phone search is one of the four ways in, so the number you searched for
// has to be visible. Order here is the default order in the table.
const COLUMNS: { id: string; label: string }[] = (() => {
    const columns = PATIENT_COLUMNS.map(({ id, label }) => ({ id, label }));
    const phoneAfter = columns.findIndex(column => column.id === "patient_type") + 1;
    columns.splice(phoneAfter, 0, { id: "main_phone", label: "Phone" });
    return columns;
})();

// What the table shows before anyone touches Manage Columns: the identifying /
// at-a-glance subset. Everything else is one dialog away, and the row context menu
// still covers the deeper actions.
const SEARCH_DEFAULT_VISIBLE_COLUMNS = new Set([
    "HM_ID",
    "first_name",
    "last_name",
    "status",
    "case_manager_id",
    "doctor_id",
    "clinic_id",
    "patient_type",
    "main_phone",
    "last_interaction",
    "next_meeting_start",
    "created_at",
    "notes",
]);

// Column order and visibility live under their own key, separate from the patients
// table's - the two tables are different sizes and shouldn't share a layout.
const COLUMN_PREFERENCES_STORAGE_KEY = "patient-search-table-preferences";

const DEFAULT_COLUMN_PREFERENCES = {
    columnOrder: COLUMNS.map(column => column.id),
    columnWidths: {},
    columnVisibility: COLUMNS.reduce((acc, column) => {
        acc[column.id] = SEARCH_DEFAULT_VISIBLE_COLUMNS.has(column.id);
        return acc;
    }, {} as Record<string, boolean>),
    sortColumn: "",
    sortOrder: "desc" as const,
};

const COLUMN_LABELS: Record<string, string> = COLUMNS.reduce((acc, column) => {
    acc[column.id] = column.label;
    return acc;
}, {} as Record<string, string>);

export const PatientSearchModal: React.FC<PatientSearchModalProps> = ({
    isOpen,
    onClose,
    user,
    onNavigate,
}) => {
    const token = localStorage.getItem("token") || "";

    const [searchBy, setSearchBy] = useState<PatientSearchBy>(() => readLastSearch().searchBy);
    const [searchInput, setSearchInput] = useState(() => readLastSearch().value);
    const [results, setResults] = useState<any[] | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Context menu + the modals its actions open, mirroring the patients table row menu.
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; patient: any } | null>(null);
    const [isLoadingPatientData, setIsLoadingPatientData] = useState(false);
    const [selectedPatientForEditInfo, setSelectedPatientForEditInfo] = useState<any>(null);
    const [isSavingPatientInfo, setIsSavingPatientInfo] = useState(false);
    const [selectedPatientForNewMeeting, setSelectedPatientForNewMeeting] = useState<any>(null);
    const [selectedPatientForViewMeetings, setSelectedPatientForViewMeetings] = useState<any>(null);
    const [selectedPatientForFinishTreatment, setSelectedPatientForFinishTreatment] = useState<any>(null);
    const [selectedPatientForPaymentLinks, setSelectedPatientForPaymentLinks] = useState<any>(null);
    const [selectedPatientForNotes, setSelectedPatientForNotes] = useState<any>(null);
    const [isSavingNotes, setIsSavingNotes] = useState(false);
    const [selectedPatientForAdHoc, setSelectedPatientForAdHoc] = useState<any>(null);
    const [selectedPatientForConfCall, setSelectedPatientForConfCall] = useState<any>(null);
    const [waPopupPatient, setWaPopupPatient] = useState<{ phone: string; name?: string; hmId?: string; id?: number } | null>(null);
    const [statusMessage, setStatusMessage] = useState<{ type: "success" | "error" | "loading"; text: string } | null>(null);

    // Column layout, persisted to localStorage. useTablePreferences also merges in
    // columns added to COLUMNS after a user's preferences were written, so a new
    // column shows up instead of being silently dropped from their saved order.
    const { preferences, updatePreferences, resetToDefaults } = useTablePreferences({
        storageKey: COLUMN_PREFERENCES_STORAGE_KEY,
        defaultPreferences: DEFAULT_COLUMN_PREFERENCES,
    });
    const [isManageColumnsOpen, setIsManageColumnsOpen] = useState(false);

    const visibleColumns = useMemo(
        () => preferences.columnOrder
            .filter(columnId => preferences.columnVisibility[columnId] && COLUMN_LABELS[columnId])
            .map(columnId => ({ id: columnId, label: COLUMN_LABELS[columnId] })),
        [preferences.columnOrder, preferences.columnVisibility]
    );

    // Reference data for the name/clinic columns and the meeting modals. All three are
    // React Query calls with the app's 5 minute stale time, so opening the popup
    // repeatedly does not re-fetch them.
    const usersQuery = useUsers({
        token,
        user_type: "clinic",
        institution_id: user?.institution_id || 0,
    });
    const clinicsQuery = useClinics(token);
    const meetingTypesQuery = useQuery({
        queryKey: ["meetingTypes", false],
        queryFn: () => apiService.getMeetingTypes(token, false),
        enabled: !!token,
        staleTime: 1000 * 60 * 5,
    });

    const usersList = useMemo(() => usersQuery.data || [], [usersQuery.data]);
    const meetingTypes = useMemo(() => meetingTypesQuery.data || [], [meetingTypesQuery.data]);

    const userNameMap = useMemo(() => {
        const map: Record<number, string> = {};
        (usersList as any[]).forEach((u: any) => {
            map[u.id] = u.name || u.email;
        });
        return map;
    }, [usersList]);

    const clinicsMap = useMemo(() => {
        const map: Record<number, string> = {};
        (clinicsQuery.data?.items || []).forEach((c: any) => {
            map[c.id] = c.name;
        });
        return map;
    }, [clinicsQuery.data]);

    // Restore the last search every time the popup is opened, but not its results -
    // they may be stale, so the user still presses Search.
    useEffect(() => {
        if (isOpen) {
            const last = readLastSearch();
            setSearchBy(last.searchBy);
            setSearchInput(last.value);
            setResults(null);
            setError(null);
            setContextMenu(null);
        }
    }, [isOpen]);

    const currentOption = SEARCH_OPTIONS.find(o => o.value === searchBy) || SEARCH_OPTIONS[0];

    const handleSearch = async () => {
        const value = searchInput.trim();
        if (value.length < MIN_SEARCH_LENGTH) return;

        saveLastSearch(searchBy, value);
        setIsSearching(true);
        setError(null);
        setContextMenu(null);
        try {
            const response = await apiService.searchPatients({ token, search_by: searchBy, value });
            setResults(response.items || []);
        } catch (e: any) {
            console.error("[PatientSearchModal] Search failed:", e);
            setError(e.message || "Search failed");
            setResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const showStatus = (type: "success" | "error", text: string) => {
        setStatusMessage({ type, text });
        setTimeout(() => setStatusMessage(null), type === "error" ? 5000 : 3000);
    };

    const goToPatient = (patientId: string | number, meetingId?: string | number) => {
        onClose();
        onNavigate?.("patient", patientId, meetingId);
    };

    const getCellContent = (patient: any, columnId: string): React.ReactNode => {
        switch (columnId) {
            case "HM_ID":
                return patient.HM_ID ? (
                    <button
                        data-ai-id="search-result-patient-link"
                        onClick={(e) => {
                            e.stopPropagation();
                            goToPatient(patient.id);
                        }}
                        className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none"
                    >
                        {patient.HM_ID}
                    </button>
                ) : "-";
            case "first_name":
                return patient.first_name || "-";
            case "last_name":
                return patient.last_name || "-";
            case "status":
                return patient.status ? (
                    <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700">{patient.status}</span>
                ) : "-";
            // Server-resolved name first: the search is global, so a hit can belong to
            // another institution, whose staff are filtered out of the /users list this
            // map is built from. The map stays as the fallback.
            case "case_manager_id":
                return patient.case_manager_name || userNameMap[patient.case_manager_id] || "-";
            case "doctor_id":
                return patient.doctor_name || userNameMap[patient.doctor_id] || "-";
            case "clinic_id":
                return clinicsMap[patient.clinic_id] || "-";
            case "patient_type":
                return patient.patient_type ? (
                    <span
                        className="px-2 py-1 text-xs rounded text-white"
                        style={{ backgroundColor: getAgeGroupColor(patient.patient_type) }}
                    >
                        {patient.patient_type}
                    </span>
                ) : "-";
            case "main_phone":
                return patient.main_phone || patient.phone || "-";
            case "last_interaction":
                return formatDate(patient.last_interaction) || "-";
            case "next_meeting_start":
                return formatNextMeetingDateTime(patient) || "-";
            case "created_at":
                return formatDate(patient.created_at) || "-";
            case "finish_reason":
                return calculateFinishReason(patient) ? (
                    <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-700">
                        {calculateFinishReason(patient)}
                    </span>
                ) : "-";
            case "invitation_status":
                return formatInvitationStatus(patient) ? (
                    <span className="px-2 py-1 text-xs rounded bg-purple-100 text-purple-700">
                        {formatInvitationStatus(patient)}
                    </span>
                ) : "-";
            // Issues and meeting number are read-only here. In the patients table each
            // opens its own modal; the search popup doesn't host those, and the row
            // context menu already reaches the patient itself. Notes are the exception -
            // they are edited in place, same pencil and popup as the patients table.
            case "issues":
                return formatTasksNumber(patient) || "-";
            case "sub_clinic":
                return formatSubClinic(patient) || "-";
            case "meeting_count":
                return formatMeetingCount(patient) || "-";
            case "notes": {
                const notes = formatNotes(patient.task);
                return (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setSelectedPatientForNotes(patient)}
                            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                            title="Edit notes"
                        >
                            ✏️
                        </button>
                        <span className="truncate max-w-xs" title={notes}>{notes}</span>
                    </div>
                );
            }
            case "funding_entity":
                return patient.funding_entity ? (
                    <span className="px-2 py-1 text-xs rounded bg-purple-100 text-purple-700">
                        {patient.funding_entity}
                    </span>
                ) : "-";
            case "move_clinic_status":
                return patient.move_clinic_status && patient.move_clinic_status !== "none" ? (
                    <span className="px-2 py-1 text-xs rounded bg-teal-100 text-teal-700">
                        {patient.move_clinic_status}
                    </span>
                ) : "-";
            case "move_type":
                return formatMoveType(patient.move_type) || "-";
            case "payment_link":
                return patient.payment_link ? (
                    <a
                        href={patient.payment_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                    >
                        Link
                    </a>
                ) : "-";
            case "area":
                return patient.area || "-";
            case "contact_name":
                return patient.contact_name || "-";
            case "contact_phone":
                return patient.contact_phone || "-";
            case "contact_role":
                return patient.contact_role || "-";
            case "cancel_reason":
                return patient.cancel_reason || "-";
            case "cancel_notes":
                return patient.cancel_notes ? (
                    <span className="truncate max-w-xs inline-block align-bottom" title={patient.cancel_notes}>
                        {patient.cancel_notes}
                    </span>
                ) : "-";
            default:
                return "-";
        }
    };

    // ---- Row context menu, same options as the patients table ----------------

    const renderContextMenu = () => {
        if (!contextMenu) return null;

        const patient = contextMenu.patient;
        const patientPhone = patient.phone;
        const hasPhoneExt = user.phone_ext && user.phone_ext.trim() !== "";

        return (
            // The shared ContextMenu sits at z-50, which is below the modal overlay
            // (z-1000). Wrapping it in a higher stacking context lifts the whole menu
            // above the popup it was opened from.
            <div className="relative z-[2000]">
            <ContextMenu
                x={contextMenu.x}
                y={contextMenu.y}
                options={[
                    {
                        label: "Edit",
                        icon: "✏️",
                        onClick: async () => {
                            setContextMenu(null);
                            setIsLoadingPatientData(true);
                            try {
                                const patientData = await apiService.getPatientPage({ token, patient_id: patient.id });
                                setSelectedPatientForEditInfo(patientData);
                            } catch (err) {
                                console.error("Failed to fetch patient data:", err);
                            } finally {
                                setIsLoadingPatientData(false);
                            }
                        }
                    },
                    ...(canScheduleMeeting(user.type) ? [{
                        label: "New Meeting",
                        icon: "📅",
                        onClick: () => {
                            setSelectedPatientForNewMeeting(patient);
                            setContextMenu(null);
                        }
                    }] : []),
                    {
                        label: "View Meetings",
                        icon: "📋",
                        onClick: () => {
                            setSelectedPatientForViewMeetings(patient);
                            setContextMenu(null);
                        }
                    },
                    ...(patient.show_schedule_link ? [{
                        label: "Open Schedule Page",
                        icon: "🗓️",
                        onClick: async () => {
                            setContextMenu(null);
                            try {
                                const res = await apiService.getPatientScheduleLink(token, Number(patient.id));
                                if (res?.link) {
                                    window.open(res.link, "_blank", "noopener");
                                }
                            } catch (err) {
                                console.error("Failed to get schedule link:", err);
                            }
                        }
                    }] : []),
                    {
                        label: "View Patient",
                        icon: "👤",
                        onClick: () => {
                            setContextMenu(null);
                            goToPatient(patient.id);
                        }
                    },
                    {
                        label: "Finish Treatment",
                        icon: (
                            <svg fill="#e23232" width="16px" height="16px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4,1A1,1,0,0,0,3,2V22a1,1,0,0,0,2,0V17H20a1,1,0,0,0,1-1V4a1,1,0,0,0-1-1H5V2A1,1,0,0,0,4,1ZM7,15H5V13H7Zm0-4H5V9H7ZM17,5h2V7H17Zm0,4h2v2H17Zm0,4h2v2H17ZM13,5h2V7H13Zm0,4h2v2H13Zm0,4h2v2H13ZM9,5h2V7H9ZM9,9h2v2H9Zm0,4h2v2H9ZM7,5V7H5V5Z" />
                            </svg>
                        ),
                        iconClassName: "",
                        onClick: () => {
                            setSelectedPatientForFinishTreatment(patient);
                            setContextMenu(null);
                        }
                    },
                    {
                        label: "Ad Hoc Meeting",
                        icon: "⚡",
                        onClick: () => {
                            setSelectedPatientForAdHoc(patient);
                            setContextMenu(null);
                        }
                    },
                    // Conference call to the patient, same as the "Call patient"
                    // button on the patient page.
                    ...(patientPhone ? [{
                        label: "Call Patient",
                        icon: "📞",
                        onClick: () => {
                            setSelectedPatientForConfCall(patient);
                            setContextMenu(null);
                        }
                    }] : []),
                    ...(patientPhone ? [{
                        label: "WhatsApp",
                        icon: "💬",
                        onClick: () => {
                            setContextMenu(null);
                            setWaPopupPatient({
                                phone: normalizePhone(patientPhone),
                                name: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
                                hmId: patient.HM_ID || patient.hm_id,
                                id: patient.id,
                            });
                        }
                    }] : []),
                    ...(hasPhoneExt && patientPhone ? [{
                        label: "Call",
                        icon: "📞",
                        onClick: async () => {
                            setContextMenu(null);
                            setStatusMessage({ type: "loading", text: "Initiating call..." });
                            try {
                                const response = await apiService.makeCall(token, patient.id, patient.main_phone);
                                showStatus("success", response.message || "Call initiated successfully");
                            } catch (err: any) {
                                console.error("Failed to make call:", err);
                                showStatus("error", err.message || err.response?.data?.message || "An error occurred");
                            }
                        }
                    }] : []),
                    // Only clinics that charge the patient carry the links, so their
                    // presence is the gate - no clinic list on the client.
                    ...((patient.payment_intake_link || patient.payment_followup_link) ? [{
                        label: "Payment Links",
                        icon: "\ud83d\udcb3",
                        onClick: () => {
                            setSelectedPatientForPaymentLinks(patient);
                            setContextMenu(null);
                        }
                    }] : []),
                    ...(patient.status === "Waiting-for-parent2-sign" ? [{
                        label: "Parent 2 consent link",
                        icon: "🔗",
                        onClick: () => {
                            const consentLink = patient.parent_2_concent_link || "";
                            if (consentLink) {
                                navigator.clipboard.writeText(consentLink);
                                showStatus("success", "Link copied to clipboard");
                            } else {
                                showStatus("error", "No consent link available");
                            }
                            setContextMenu(null);
                        }
                    }] : []),
                    ...(patient.status === "Waiting-for-single-guardian-verification" ? [{
                        label: "Single guardian verification link",
                        icon: "🔗",
                        onClick: () => {
                            const verificationLink = patient.single_guardian_link || "";
                            if (verificationLink) {
                                navigator.clipboard.writeText(verificationLink);
                                showStatus("success", "Link copied to clipboard");
                            } else {
                                showStatus("error", "No verification link available");
                            }
                            setContextMenu(null);
                        }
                    }] : [])
                ]}
                onClose={() => setContextMenu(null)}
            />
            </div>
        );
    };

    const renderResults = () => {
        if (isSearching) {
            return (
                <div className="text-center py-10">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600 mt-3">Searching...</p>
                </div>
            );
        }

        if (error) {
            return <div className="text-center py-10 text-red-600">{error}</div>;
        }

        if (results === null) {
            return (
                <div className="text-center py-10 text-gray-500">
                    Choose what to search by, type at least {MIN_SEARCH_LENGTH} characters and press Search.
                </div>
            );
        }

        if (results.length === 0) {
            return <div className="text-center py-10 text-gray-600">No patients found</div>;
        }

        return (
            <div className="overflow-x-auto max-h-[45vh] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                            {visibleColumns.map(column => (
                                <th
                                    key={column.id}
                                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                                >
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {results.map((patient: any) => (
                            <tr
                                key={patient.id}
                                className="hover:bg-gray-50 cursor-pointer"
                                onClick={(e) => {
                                    // Don't open the context menu when a link/button in the row was clicked.
                                    const target = e.target as HTMLElement;
                                    if (
                                        target.tagName === "BUTTON" ||
                                        target.tagName === "A" ||
                                        target.closest("button") ||
                                        target.closest("a")
                                    ) {
                                        return;
                                    }
                                    setContextMenu({ x: e.clientX, y: e.clientY, patient });
                                }}
                            >
                                {visibleColumns.map(column => (
                                    <td
                                        key={column.id}
                                        className={`px-4 py-3 whitespace-nowrap text-sm ${
                                            column.id === "sub_clinic" && shouldHighlightSubClinic(patient)
                                                ? "text-red-600 font-medium"
                                                : "text-gray-900"
                                        }`}
                                    >
                                        {getCellContent(patient, column.id)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <>
            <ModalWrapper modalId="PatientSearchModal"
                isOpen={isOpen}
                onClose={onClose}
                title="Search Patient"
                width="max-w-6xl"
                titleAlign="left"
            >
                <div className="space-y-4">
                    <div className="flex flex-wrap gap-3 items-end">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Search by</label>
                            <select
                                value={searchBy}
                                onChange={(e) => {
                                    setSearchBy(e.target.value as PatientSearchBy);
                                    setResults(null);
                                    setError(null);
                                }}
                                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                                {SEARCH_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex-1 min-w-[220px]">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                            <input
                                type="text"
                                autoFocus
                                placeholder={currentOption.placeholder}
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSearch();
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <button
                            onClick={handleSearch}
                            disabled={isSearching || searchInput.trim().length < MIN_SEARCH_LENGTH}
                            className="px-5 py-2 rounded-md text-white font-medium bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Search
                        </button>
                        <button
                            onClick={() => setIsManageColumnsOpen(true)}
                            className="px-5 py-2 rounded-md font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
                        >
                            Manage Columns
                        </button>
                    </div>

                    {searchInput.trim().length > 0 && searchInput.trim().length < MIN_SEARCH_LENGTH && (
                        <div className="text-sm text-[#366A75]">
                            Type at least {MIN_SEARCH_LENGTH} characters to search.
                        </div>
                    )}

                    {results !== null && !isSearching && !error && (
                        <div className="text-sm text-gray-500">{results.length} result{results.length === 1 ? "" : "s"}</div>
                    )}

                    {renderResults()}
                </div>
            </ModalWrapper>

            {renderContextMenu()}

            <ManageColumnsModal
                isOpen={isManageColumnsOpen}
                onClose={() => setIsManageColumnsOpen(false)}
                columns={preferences.columnOrder
                    .filter(id => COLUMN_LABELS[id])
                    .map(id => ({
                        id,
                        label: COLUMN_LABELS[id],
                        visible: preferences.columnVisibility[id] ?? false,
                    }))}
                onSave={(columns: ColumnConfig[]) => {
                    updatePreferences({
                        columnOrder: columns.map(column => column.id),
                        columnVisibility: columns.reduce((acc, column) => {
                            acc[column.id] = column.visible;
                            return acc;
                        }, {} as Record<string, boolean>),
                    });
                }}
                onReset={resetToDefaults}
            />

            {isLoadingPatientData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                    <div className="bg-white rounded-lg p-6 flex flex-col items-center shadow-2xl">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                        <p className="text-gray-700 font-medium">Loading patient data...</p>
                    </div>
                </div>
            )}

            <EditPatientModal
                isOpen={!!selectedPatientForEditInfo}
                onClose={() => setSelectedPatientForEditInfo(null)}
                patient={selectedPatientForEditInfo}
                onSave={async (updatedData: any) => {
                    setIsSavingPatientInfo(true);
                    try {
                        await apiService.updatePatientInfo(token, selectedPatientForEditInfo.id, updatedData);
                        setSelectedPatientForEditInfo(null);
                        await handleSearch();
                    } catch (err: any) {
                        console.error("Failed to update patient info:", err);
                        throw new Error(err.response?.data?.message || err.message || "Failed to update patient information");
                    } finally {
                        setIsSavingPatientInfo(false);
                    }
                }}
                isSaving={isSavingPatientInfo}
            />

            <NewMeetingModal
                isOpen={!!selectedPatientForNewMeeting}
                onClose={() => setSelectedPatientForNewMeeting(null)}
                patient={selectedPatientForNewMeeting}
                users={usersList as any}
                meetingTypes={meetingTypes}
                currentUser={user}
            />

            <ViewMeetingsModal
                isOpen={!!selectedPatientForViewMeetings}
                onClose={() => setSelectedPatientForViewMeetings(null)}
                patientId={selectedPatientForViewMeetings?.id || ""}
                patientName={selectedPatientForViewMeetings ? `${selectedPatientForViewMeetings.first_name} ${selectedPatientForViewMeetings.last_name}` : ""}
                user={user}
                token={token}
                users={usersList as any}
                meetingTypes={meetingTypes}
            />

            <FinishTreatmentModal
                isOpen={!!selectedPatientForFinishTreatment}
                onClose={() => setSelectedPatientForFinishTreatment(null)}
                patientId={selectedPatientForFinishTreatment?.id || 0}
                token={token}
                onSuccess={() => { handleSearch(); }}
            />

            <PaymentLinksModal
                isOpen={!!selectedPatientForPaymentLinks}
                onClose={() => setSelectedPatientForPaymentLinks(null)}
                patientName={selectedPatientForPaymentLinks
                    ? `${selectedPatientForPaymentLinks.first_name || ""} ${selectedPatientForPaymentLinks.last_name || ""}`.trim()
                    : ""}
                intakeLink={selectedPatientForPaymentLinks?.payment_intake_link}
                followupLink={selectedPatientForPaymentLinks?.payment_followup_link}
                onStatusMessage={(msg) => {
                    if (msg) showStatus(msg.type === "error" ? "error" : "success", msg.text);
                }}
            />

            <EditCommentsModal
                isOpen={!!selectedPatientForNotes}
                onClose={() => setSelectedPatientForNotes(null)}
                initialNotes={selectedPatientForNotes?.task || ""}
                onSave={async (newNotes: string) => {
                    setIsSavingNotes(true);
                    try {
                        await apiService.updatePatientComments(token, selectedPatientForNotes.id, newNotes);
                        // Patch the row in place rather than re-running the search: the
                        // note is the only thing that changed, and a fresh search would
                        // throw away the scroll position for it.
                        const patientId = selectedPatientForNotes.id;
                        setResults(prev => prev
                            ? prev.map(row => (row.id === patientId ? { ...row, task: newNotes } : row))
                            : prev);
                        setSelectedPatientForNotes(null);
                    } catch (err: any) {
                        console.error("Failed to update notes:", err);
                        throw new Error(err.response?.data?.message || "Failed to update notes");
                    } finally {
                        setIsSavingNotes(false);
                    }
                }}
                isSaving={isSavingNotes}
            />

            {selectedPatientForAdHoc && (
                <AdHocMeetingModal
                    patientId={Number(selectedPatientForAdHoc.id)}
                    token={token}
                    meetingTypes={meetingTypes}
                    onClose={() => setSelectedPatientForAdHoc(null)}
                    onSuccess={(meetingId) => {
                        const patientId = selectedPatientForAdHoc.id;
                        setSelectedPatientForAdHoc(null);
                        goToPatient(patientId, meetingId);
                    }}
                    onError={(msg) => showStatus("error", msg)}
                />
            )}

            {selectedPatientForConfCall && (
                <CallPatientConfModal
                    patientId={selectedPatientForConfCall.id}
                    patientName={`${selectedPatientForConfCall.first_name || ""} ${selectedPatientForConfCall.last_name || ""}`.trim()}
                    patientPhone={selectedPatientForConfCall.phone}
                    parent1Phone={selectedPatientForConfCall.parent_1_phone_number}
                    parent2Phone={selectedPatientForConfCall.parent_2_phone_number}
                    token={token}
                    onClose={() => setSelectedPatientForConfCall(null)}
                    onSuccess={() => showStatus("success", "Call initiated successfully!")}
                    onError={(msg) => showStatus("error", msg)}
                />
            )}

            {waPopupPatient && (
                <WhatsAppConversationPopup
                    phoneNumber={waPopupPatient.phone}
                    patientName={waPopupPatient.name}
                    patientHmId={waPopupPatient.hmId}
                    patientId={waPopupPatient.id}
                    currentUserId={Number(user.id)}
                    onClose={() => setWaPopupPatient(null)}
                />
            )}

            {statusMessage && (
                <div
                    className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-[10000] px-6 py-3 rounded-lg shadow-lg"
                    style={{
                        backgroundColor:
                            statusMessage.type === "error" ? "#fee2e2" :
                                statusMessage.type === "success" ? "#dcfce7" : "#f3f4f6",
                        color:
                            statusMessage.type === "error" ? "#dc2626" :
                                statusMessage.type === "success" ? "#16a34a" : "#6b7280",
                    }}
                >
                    {statusMessage.text}
                </div>
            )}
        </>
    );
};
